import * as Sentry from '@sentry/react'
import moment from 'moment'

import { campaignStatusResponseSchema } from './schemas'

// Google reports ENABLED/PAUSED, Meta reports ACTIVE/PAUSED — unify to
// ACTIVE/PAUSED for a single display vocabulary, per the guide's explicit
// "normalise these in the display layer if showing one badge" note.
export function normalizePlatformCampaignStatus(platform, rawStatus) {
  if (!rawStatus) {
    return null
  }
  if (platform === 'google') {
    return rawStatus === 'ENABLED' ? 'ACTIVE' : 'PAUSED'
  }
  return rawStatus
}

// Mirrors v3's hasEndedMarketingPlatformCampaign/hasActiveMarketingPlatformCampaign/
// hasPausedMarketingPlatformCampaign, but per-platform: "ended" (stop/end date
// passed) overrides whatever status the platform last synced, same as v3's
// intent — a synced ACTIVE/PAUSED status can be stale past the campaign's own
// end date until the next import cycle catches up.
function buildPlatformState(block, { platform, endDateField }) {
  if (!block) {
    // Platform key absent from `platforms` — not yet published/synced on
    // this platform. Must not be conflated with "paused".
    return {
      published: false,
      status: null,
      rawStatus: null,
      hasActive: false,
      hasPaused: false,
      hasEnded: false,
      task: null,
      intent: null,
    }
  }

  const rawStatus = block.campaign?.status ?? null
  const status = normalizePlatformCampaignStatus(platform, rawStatus)
  const endValue = block.campaign?.[endDateField]
  const hasEnded = !!endValue && moment(endValue).isBefore(moment())

  return {
    published: true,
    status,
    rawStatus,
    hasActive: !hasEnded && status === 'ACTIVE',
    hasPaused: !hasEnded && status === 'PAUSED',
    hasEnded,
    task: block.task ?? null,
    intent: block.intent ?? null,
  }
}

// v4 response -> FE domain model, replacing v3's getEventMarketingCampaignState
// (api/models/event.js — still used by EventHeader/EventRow, which haven't
// migrated off the flat `event` object yet). Named mapCampaignStatusResponse,
// not the same name as that function, since the two aren't interchangeable:
// this takes this module's fetchAsync response (the parsed GET
// /campaign_status body), and v4 splits active/paused/ended per platform
// instead of collapsing it into one status.
export function mapCampaignStatusResponse(data) {
  const parsed = campaignStatusResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.captureException(parsed.error)
  }
  const source = parsed.success ? parsed.data : data
  const platforms = source.platforms ?? {}

  const google = {
    ...buildPlatformState(platforms.google, { platform: 'google', endDateField: 'end_date' }),
    adGroups: platforms.google?.ad_groups ?? [],
  }
  const meta = {
    ...buildPlatformState(platforms.meta, { platform: 'meta', endDateField: 'stop_time' }),
    adSets: platforms.meta?.ad_sets ?? [],
  }

  return {
    clientCampaignStatus: source.client_campaign_status ?? null,
    campaignStatus: source.campaign_status ?? null,
    campaignStartTime: source.campaign_start_time ?? null,
    campaignStopTime: source.campaign_stop_time ?? null,
    task: source.task ?? null,
    google,
    meta,
    // Parallels v3's hasMarketingCampaign (hasActiveCampaign || hasPausedCampaign
    // || hasEndedCampaign), OR'd across both platforms instead of one flat status.
    hasAnyMarketingCampaign:
      google.hasActive ||
      google.hasPaused ||
      google.hasEnded ||
      meta.hasActive ||
      meta.hasPaused ||
      meta.hasEnded,
  }
}

const IN_FLIGHT_TASK_STATES = ['PENDING', 'STARTED']

// Per the guide: show a loading indicator while a platform's task.state is
// PENDING/STARTED, refresh by polling fetchAsync until terminal.
export function isCampaignStatusTaskInFlight(task) {
  return !!task && IN_FLIGHT_TASK_STATES.includes(task.state)
}
